/**
 * useGatedAnalytics Hook
 *
 * Analytics tracking for blur teaser paywall interactions.
 * Tracks conversion funnel: Shown → Engaged → Started → Completed/Dismissed
 *
 * @see lib/analytics/events/paywall.ts for event definitions
 */

import { useCallback, useRef, useEffect } from 'react';
import { trackEvent } from '@/lib/analytics/events';
import type { ContentType } from '@/lib/analytics/events/types';
import type { UserTier, RequiredTier, BlurIntensity } from './types';

interface UseGatedAnalyticsProps {
  contentType: ContentType;
  requiredTier: RequiredTier;
  currentTier: UserTier;
  screen: string;
  blurIntensity: BlurIntensity;
  gatedCount?: number;
  isGated: boolean;
}

interface UseGatedAnalyticsReturn {
  /** Call when blur teaser becomes visible */
  trackBlurShown: () => void;
  /** Call when user engages with blur teaser */
  trackBlurEngaged: (interactionType: 'tap' | 'hover' | 'scroll_into_view') => void;
  /** Call when user clicks upgrade CTA */
  trackUpgradeStarted: (triggerSource: 'blur_teaser' | 'upgrade_prompt') => void;
  /** Call when user dismisses upgrade prompt */
  trackUpgradeDismissed: (dismissMethod: 'close_button' | 'tap_outside' | 'back_navigation' | 'swipe') => void;
}

export function useGatedAnalytics({
  contentType,
  requiredTier,
  currentTier,
  screen,
  blurIntensity,
  gatedCount,
  isGated,
}: UseGatedAnalyticsProps): UseGatedAnalyticsReturn {
  // Track when blur was first shown for time calculations
  const shownAtRef = useRef<number | null>(null);
  const hasTrackedShownRef = useRef(false);

  // Track Paywall_Blur_Shown on mount (once per component instance)
  useEffect(() => {
    if (isGated && !hasTrackedShownRef.current) {
      shownAtRef.current = Date.now();
      hasTrackedShownRef.current = true;

      trackEvent('Paywall_Blur_Shown', {
        content_type: contentType,
        required_tier: requiredTier,
        current_tier: currentTier,
        screen,
        gated_count: gatedCount,
        blur_intensity: blurIntensity,
      });
    }
  }, [isGated, contentType, requiredTier, currentTier, screen, gatedCount, blurIntensity]);

  const trackBlurShown = useCallback(() => {
    // Validate tier values before tracking (analytics data integrity)
    if (!currentTier || !requiredTier) {
      if (__DEV__) {
        console.warn('[useGatedAnalytics] Invalid tier values, skipping Blur_Shown event');
      }
      return;
    }

    // Already tracked via useEffect, but expose for manual calls if needed
    if (!hasTrackedShownRef.current) {
      shownAtRef.current = Date.now();
      hasTrackedShownRef.current = true;

      trackEvent('Paywall_Blur_Shown', {
        content_type: contentType,
        required_tier: requiredTier,
        current_tier: currentTier,
        screen,
        gated_count: gatedCount,
        blur_intensity: blurIntensity,
      });
    }
  }, [contentType, requiredTier, currentTier, screen, gatedCount, blurIntensity]);

  const trackBlurEngaged = useCallback(
    (interactionType: 'tap' | 'hover' | 'scroll_into_view') => {
      // Validate tier values before tracking
      if (!currentTier || !requiredTier) {
        if (__DEV__) {
          console.warn('[useGatedAnalytics] Invalid tier values, skipping Blur_Engaged event');
        }
        return;
      }

      const timeToEngage = shownAtRef.current ? Date.now() - shownAtRef.current : undefined;

      trackEvent('Paywall_Blur_Engaged', {
        content_type: contentType,
        required_tier: requiredTier,
        current_tier: currentTier,
        screen,
        interaction_type: interactionType,
        time_to_engage_ms: timeToEngage,
      });
    },
    [contentType, requiredTier, currentTier, screen]
  );

  const trackUpgradeStarted = useCallback(
    (triggerSource: 'blur_teaser' | 'upgrade_prompt') => {
      trackEvent('Paywall_Upgrade_Started', {
        content_type: contentType,
        target_tier: requiredTier,
        current_tier: currentTier,
        screen,
        trigger_source: triggerSource,
      });
    },
    [contentType, requiredTier, currentTier, screen]
  );

  const trackUpgradeDismissed = useCallback(
    (dismissMethod: 'close_button' | 'tap_outside' | 'back_navigation' | 'swipe') => {
      const timeVisible = shownAtRef.current ? Date.now() - shownAtRef.current : undefined;

      trackEvent('Paywall_Upgrade_Dismissed', {
        content_type: contentType,
        target_tier: requiredTier,
        current_tier: currentTier,
        screen,
        dismiss_method: dismissMethod,
        time_visible_ms: timeVisible,
      });
    },
    [contentType, requiredTier, currentTier, screen]
  );

  return {
    trackBlurShown,
    trackBlurEngaged,
    trackUpgradeStarted,
    trackUpgradeDismissed,
  };
}
